import { ContentContainer } from 'components/ContentContainer';
import { FC } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Heading, Paragraph, Reservation, ReservationCard } from '@expressable/syntax-ds';
import { HeadingCombo } from 'domains/signupDirect/SyntaxComps/HeadingCombo';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { DATE_LONG_DAY_MONTH_YEAR, TIME_ONLY_WITH_ZONE_SHORT } from 'utils/date';
import { TherapistList } from './TherapistList';
import { SmallTherapist } from 'domains/calendars/types';
import { StateAbbrev } from 'utils/states';
import { Reschedule } from 'domains/direct-reschedule/types';

type Props = {
  rescheduleInfo: Reschedule;
  onConfirmStep: () => void;
  onEditReservation: () => void;
  therapists?: SmallTherapist[];
  state?: StateAbbrev;
  clinicalAreas: string[];
  insuranceName?: string;
};

export const SelectTherapistUi: FC<Props> = ({
  rescheduleInfo,
  onConfirmStep,
  onEditReservation,
  therapists,
  state,
  clinicalAreas,
  insuranceName,
}) => {
  const { control, setValue } = useFormContext();
  const selectedTimeSlot = useWatch({ control, name: 'timeSlot' });

  const { t } = useTranslation();
  const { format } = useDisplayTimezone();

  const onSubmitStep = (calendarId: string) => {
    setValue('calendarId', calendarId);
    onConfirmStep();
  };

  return (
    <ContentContainer variant="full">
      <ContentContainer variant="centered-md" className="items-stretch">
        <div className="mb-64">
          <HeadingCombo>
            <Heading noDefaultMargins level="h1">
              {t('rescheduleDirect.selectTherapist.title')}
            </Heading>
            <Paragraph variant="secondary">
              {t(
                'rescheduleDirect.selectTherapist.subtitle',
              )}
            </Paragraph>
          </HeadingCombo>
        </div>

        <ReservationCard
          className="mb-64"
          title={t('rescheduleDirect.selectTherapist.reservationCard.main')}
        >
          <Reservation
            ctaLabel={t('rescheduleDirect.selectTherapist.reservationCard.cta')}
            dateLabel={format(selectedTimeSlot, DATE_LONG_DAY_MONTH_YEAR)}
            onCtaClick={() => {
              setValue('timeSlot', undefined);
              setValue('day', undefined);
              onEditReservation();
            }}
            timeLabel={format(selectedTimeSlot, TIME_ONLY_WITH_ZONE_SHORT)}
            title={t('rescheduleDirect.selectTherapist.reservationCard.title', {
              duration: rescheduleInfo.originalAcuityAppointment.duration,
            })}
          />
        </ReservationCard>

        <TherapistList
          therapists={therapists}
          state={state}
          clinicalAreas={clinicalAreas}
          insuranceName={insuranceName}
          onConfirm={onSubmitStep}
        />
      </ContentContainer>
    </ContentContainer>
  );
};
